import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, FileText, RefreshCw } from "lucide-react";
import API from "../../services/api";
import { downloadWithAuth, nzToday, addDay, prettyDate } from "../runsheets/util";
import { currentFortnight } from "../timesheets/util";
import {
  EXPIRY_FIELDS, ExpiryChip, StatusChip, FaultChips, FaultRow, ActionForm, VoidForm,
  CheckComments, VoidedLine, RemoveButton, shortDate, fmtHubo, sortNewest,
} from "./util";

/* One truck's paper trail: every daily check in a date range, the faults
 * still waiting on the office regardless of date, and the button that prints
 * the range back out as the NZTA form.
 *
 * The range defaults to the pay fortnight because that is the window the
 * office already thinks in. Open faults are listed outside the range on
 * purpose: a fault from six weeks ago that nobody actioned must not hide
 * behind a date picker.
 *
 * Removed (voided) checks are asked for on every load and kept apart: they
 * are never in the count, the open-fault list or the print, but "Show
 * removed (n)" can lay them back into the table, dimmed, with who removed
 * them and why - the office can always see what was taken off the sheet.
 */
export default function TruckHistory({ truck, onBack, onChanged, reportError, notify }) {
  const [range, setRange] = useState(() => currentFortnight(nzToday()));
  const [checks, setChecks] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [showRemoved, setShowRemoved] = useState(false);
  const [open, setOpen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Which check has a form open, which form, and where it was opened from -
  // the same fault can sit in the open-faults panel and in the table, and
  // the form must appear under the button the operator actually pressed.
  const [formFor, setFormFor] = useState(null);   // { id, where: "open" | "table", kind: "action" | "void" }
  const formOf = (id, where) => (formFor?.id === id && formFor.where === where ? formFor.kind : null);
  const isOpen = (id, where, kind) => formOf(id, where) === kind;
  const toggle = (id, where, kind) => setFormFor(isOpen(id, where, kind) ? null : { id, where, kind });
  const closeForm = () => setFormFor(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inRange, faults] = await Promise.all([
        // include_voided so the "Show removed (n)" count is known without a
        // second round trip; the split below keeps them out of everything
        // but that toggle. An API build that ignores the flag sends none.
        API.get(`/checks?truck_id=${truck.id}&from=${range.start}&to=${range.end}&include_voided=1`),
        API.get(`/checks?truck_id=${truck.id}&status=fault`),
      ]);
      const all = sortNewest(inRange.data?.checks);
      setChecks(all.filter((c) => !c.voided));
      setRemoved(all.filter((c) => c.voided));
      setOpen(sortNewest(faults.data?.checks).filter((c) => !c.voided));
    } catch (err) {
      reportError(err);
    } finally {
      setLoading(false);
    }
  }, [truck.id, range.start, range.end, reportError]);

  useEffect(() => { load(); }, [load]);

  // An action recorded here changes the fleet view too (open fault counts,
  // the red highlight), so the parent reloads as well as this list.
  const actionDone = async () => {
    setFormFor(null);
    notify("Action recorded. The check keeps its fault; the truck no longer shows it as open.");
    await Promise.all([load(), onChanged()]);
  };

  // A removed check leaves the fleet's last-check and open-fault figures
  // too, so the parent reloads as well.
  const voidDone = async () => {
    setFormFor(null);
    notify("Check removed. It stays on record, marked removed by you with your reason - see \"Show removed\".");
    await Promise.all([load(), onChanged()]);
  };

  // Removed rows are laid back in date order, not appended, so the office
  // sees where on the sheet the removed check sat.
  const rows = showRemoved ? sortNewest([...checks, ...removed]) : checks;

  // Never a plain <a href>: it carries no token and 401s.
  const print = async () => {
    setBusy(true);
    try {
      const plate = String(truck.license_plate || truck.id).replace(/[^A-Za-z0-9-]+/g, "");
      await downloadWithAuth(
        `/trucks/${truck.id}/checks.pdf?from=${range.start}&to=${range.end}`,
        `checklist_${plate}_${range.start}_${range.end}.pdf`);
      notify(`Downloaded the checklist for ${truck.license_plate || truck.id}, ${range.start} to ${range.end}.`);
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  const setStart = (v) => setRange((r) => ({ ...r, start: v || r.start }));
  const setEnd = (v) => setRange((r) => ({ ...r, end: v || r.end }));
  const shift = (n) => setRange((r) => ({ start: addDay(r.start, n), end: addDay(r.end, n) }));

  const header = [
    ["Make", truck.make], ["Fleet No", truck.fleet_no], ["TSL", truck.tsl], ["Service due", truck.service_due],
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <button onClick={onBack}
            className="p-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-300 hover:text-white hover:border-slate-600 transition-all"
            title="Back to the fleet">
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-xl font-black text-white tracking-tight">{truck.license_plate || truck.id}</h3>
              {truck.is_active === false && (
                <span className="px-2 py-0.5 rounded border text-[10px] font-bold text-slate-400 border-slate-700 bg-slate-800/60">retired</span>
              )}
              {truck.open_faults > 0 && (
                <span className="px-2 py-0.5 rounded border text-[10px] font-bold text-red-200 border-red-500/50 bg-red-500/15">
                  {truck.open_faults} open fault{truck.open_faults === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-400">
              {header.map(([k, v]) => (
                <span key={k}><span className="text-slate-500">{k}:</span> <span className="text-slate-200 font-medium">{v || "—"}</span></span>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {EXPIRY_FIELDS.map((f) => <ExpiryChip key={f.key} truck={truck} field={f.key} label={f.label} />)}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <button onClick={() => shift(-14)} className="text-slate-400 hover:text-white underline">← fortnight</button>
          <input type="date" value={range.start} max={range.end} onChange={(e) => setStart(e.target.value)}
            className="bg-slate-950 border border-slate-700/80 rounded-lg px-2 py-1.5 text-slate-200 outline-none focus:border-blue-500" />
          <span className="text-slate-500">to</span>
          <input type="date" value={range.end} min={range.start} onChange={(e) => setEnd(e.target.value)}
            className="bg-slate-950 border border-slate-700/80 rounded-lg px-2 py-1.5 text-slate-200 outline-none focus:border-blue-500" />
          <button onClick={() => shift(14)} className="text-slate-400 hover:text-white underline">fortnight →</button>
          <button onClick={() => setRange(currentFortnight(nzToday()))} className="text-slate-400 hover:text-white underline">This fortnight</button>
          <button onClick={load} disabled={loading}
            className="p-1.5 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-300 hover:text-white hover:border-slate-600 transition-all disabled:opacity-50"
            title="Reload">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
          <button onClick={print} disabled={busy}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-bold bg-slate-900/60 border border-slate-800 text-slate-200 hover:border-slate-600 transition-all disabled:opacity-50"
            title="The NZTA daily checklist for this range, one column per day">
            <FileText size={14} /> Print checklist (.pdf)
          </button>
        </div>
      </div>

      {/* Faults still waiting on the office - any date, on purpose */}
      {open.length > 0 && (
        <div className="bg-slate-950/40 rounded-2xl border border-red-500/30 p-4 space-y-2">
          <h4 className="font-bold text-red-200 text-sm border-b border-slate-800 pb-2">
            Open faults on this truck ({open.length})
          </h4>
          {open.map((c) => (
            <FaultRow key={c.id} check={c}
              form={formOf(c.id, "open")}
              onToggleForm={(kind) => toggle(c.id, "open", kind)}
              onDone={actionDone} onVoided={voidDone} onCancel={closeForm} reportError={reportError} />
          ))}
        </div>
      )}

      {/* The record for the range */}
      <div className="bg-slate-950/40 rounded-2xl border border-slate-800/80 p-4 space-y-3">
        <h4 className="font-bold text-white text-sm border-b border-slate-800 pb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>
            Daily checks · {prettyDate(range.start)} → {prettyDate(range.end)}
            <span className="text-slate-500 font-normal"> · {checks.length} check{checks.length === 1 ? "" : "s"}</span>
          </span>
          {removed.length > 0 && (
            <label className="ml-auto flex items-center gap-1.5 text-xs font-normal text-slate-400 cursor-pointer"
              title="Checks taken off the sheet by the office. They are kept, marked with who removed them and why, and never counted or printed.">
              <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} />
              Show removed ({removed.length})
            </label>
          )}
        </h4>
        {loading && !rows.length ? (
          <p className="text-xs text-slate-500 py-4 text-center">Loading…</p>
        ) : !rows.length ? (
          <p className="text-xs text-slate-500 py-4 text-center bg-slate-900/10 rounded-xl border border-dashed border-slate-800">
            No checks recorded for this truck in this range.
            {removed.length > 0 ? ` ${removed.length} removed - tick "Show removed" to see ${removed.length === 1 ? "it" : "them"}.` : ""}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-800/80">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-900/60 text-slate-400 uppercase font-semibold text-[10px]">
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Driver</th>
                  <th className="px-3 py-2 text-right">Hubo</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Faults</th>
                  <th className="px-3 py-2 text-left">Faults / comments</th>
                  <th className="px-3 py-2 text-left">Action taken</th>
                  <th className="px-3 py-2 text-left"><span className="sr-only">Remove</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <React.Fragment key={c.id}>
                    <tr className={`border-t border-slate-800/60 align-top ${
                      c.voided ? "opacity-50 bg-slate-900/20"
                        : c.status === "fault" ? "bg-red-500/5" : "hover:bg-slate-900/30"}`}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-semibold text-slate-100">{shortDate(c.check_date)}</div>
                        <div className="text-[10px] text-slate-500">
                          {c.submitted_nz || ""}{c.source && c.source !== "app" ? ` · ${c.source}` : ""}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-200 whitespace-nowrap">{c.person_name || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-300">{fmtHubo(c.hubo_reading)}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-1">
                          <StatusChip status={c.status} />
                          {c.voided && (
                            <span className="inline-block px-2 py-0.5 rounded border text-[10px] font-bold tracking-wide whitespace-nowrap text-slate-400 border-slate-700 bg-slate-800/60">
                              REMOVED
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 min-w-[160px]"><FaultChips faults={c.faults} notes={c.item_notes} /></td>
                      <td className="px-3 py-2 min-w-[160px]"><CheckComments check={c} /></td>
                      <td className="px-3 py-2 min-w-[180px]">
                        {c.action_taken ? (
                          <div className="text-slate-300 whitespace-pre-wrap">
                            {c.action_taken}
                            <div className="text-[10px] text-slate-500 mt-0.5">{c.action_by || "—"} · {c.action_at_nz || "—"}</div>
                          </div>
                        ) : c.status === "fault" && !c.voided ? (
                          <button onClick={() => toggle(c.id, "table", "action")}
                            className="text-sky-300 hover:text-sky-200 px-2 py-1 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/25 rounded-lg transition-all font-semibold whitespace-nowrap">
                            {isOpen(c.id, "table", "action") ? "Close" : "Record action taken"}
                          </button>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {/* A removed check cannot be removed again; its audit line takes the control's place. */}
                        {c.voided
                          ? <VoidedLine check={c} />
                          : <RemoveButton open={isOpen(c.id, "table", "void")} onClick={() => toggle(c.id, "table", "void")} />}
                      </td>
                    </tr>
                    {formOf(c.id, "table") && !c.voided && (
                      <tr className="border-t border-slate-800/60 bg-slate-900/30">
                        <td colSpan={8} className="px-3 py-2">
                          {formOf(c.id, "table") === "void"
                            ? <VoidForm check={c} onDone={voidDone} onCancel={closeForm} reportError={reportError} />
                            : <ActionForm check={c} onDone={actionDone} onCancel={closeForm} reportError={reportError} />}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[10px] text-slate-500">
          Driver sign = the signed-in account plus the server time it was submitted. Faulted items are the driver's
          crosses on the form; safety-critical ones (brakes, steering, tyres, front and rear lights) are in red.
          "Remove" takes a check off every screen and print but keeps the record, marked with who removed it and why.
        </p>
      </div>
    </div>
  );
}
